import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { marked } from 'marked';
import axios from 'axios';
import Footer from '../components/Footer';
import PostIllustration from '../components/PostIllustration';
import ShareButtons from '../components/ShareButtons';

interface Post {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  tags: string[];
  image_url?: string;
  published_at: string;
}

const FONT = '"Plus Jakarta Sans", system-ui, sans-serif';

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/blog/${slug}`)
      .then(r => setPost(r.data))
      .catch(err => { if (err.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F6F1E3' }}>
        <div className="w-8 h-8 rounded-full border-4 animate-spin" style={{ borderColor: '#0F3D2E', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#F6F1E3' }}>
        <div className="text-center">
          <p className="text-2xl font-bold mb-3" style={{ color: 'rgb(22,30,45)', fontFamily: FONT }}>Post not found</p>
          <Link to="/blog" className="text-sm font-semibold" style={{ color: '#0F3D2E', fontFamily: FONT }}>← Back to Blog</Link>
        </div>
      </div>
    );
  }

  // Read time
  const wordCount = post.content.trim().split(/\s+/).length;
  const readTime = Math.ceil(wordCount / 200);

  const postUrl = `https://cloudfrugal.app/blog/${post.slug}`;

  // Extract ## headings for TOC
  const headings = post.content
    .split('\n')
    .filter(l => l.startsWith('## '))
    .map(l => {
      const text = l.replace(/^## /, '').trim();
      return { text, id: text.toLowerCase().replace(/[^a-z0-9]+/g, '-') };
    });

  // Render markdown with heading IDs for anchor links
  marked.use({
    renderer: {
      heading({ text, depth }: { text: string; depth: number }) {
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        return `<h${depth} id="${id}">${text}</h${depth}>`;
      }
    }
  });
  // Strip trailing --- (from truncated Gemini delimiters) that marked interprets as setext h2
  const cleanContent = post.content.replace(/\n---\s*$/, '');
  const html = marked.parse(cleanContent) as string;

  return (
    <div className="min-h-screen" style={{ background: '#F6F1E3', fontFamily: FONT }}>

      {/* Hero — illustration as background */}
      <div className="relative overflow-hidden py-20 px-4" style={{ minHeight: '320px' }}>
        {/* Background illustration */}
        <PostIllustration
          tags={post.tags}
          title={post.title}
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: 'cover' }}
        />
        {/* Dark overlay for text legibility */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(7,40,29,0.92) 0%, rgba(7,40,29,0.7) 60%, rgba(7,40,29,0.4) 100%)' }} />

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold mb-8" style={{ color: '#E8B84C', fontFamily: FONT }}>
            ← Back to Blog
          </Link>
          <div className="flex flex-wrap gap-2 mb-5">
            {post.tags.map(tag => (
              <span key={tag} className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: 'rgba(232,184,76,0.15)', color: '#E8B84C', fontFamily: FONT }}>
                {tag}
              </span>
            ))}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: FONT, letterSpacing: '-0.02em', maxWidth: '680px' }}>
            {post.title}
          </h1>
          <div className="flex items-center gap-2 text-sm mb-6" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: FONT }}>
            <span>{new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            <span>·</span>
            <span>{readTime} min read</span>
          </div>
          <ShareButtons url={postUrl} title={post.title} theme="light" />
        </div>
      </div>

      {/* Content + TOC */}
      <div className="max-w-5xl mx-auto px-4 py-12 lg:grid lg:gap-12" style={{ gridTemplateColumns: headings.length > 2 ? '200px 1fr' : '1fr' }}>

        {/* Sticky TOC — desktop only, only if 3+ headings */}
        {headings.length > 2 && (
          <aside className="hidden lg:block">
            <div className="sticky top-8">
              <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: 'rgba(15,22,32,0.35)', fontFamily: FONT }}>
                On this page
              </p>
              <nav className="flex flex-col gap-0.5">
                {headings.map(h => (
                  <a
                    key={h.id}
                    href={`#${h.id}`}
                    className="text-sm py-1.5 px-2 rounded-lg transition-colors hover:bg-white"
                    style={{ color: 'rgba(15,22,32,0.5)', fontFamily: FONT }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#0F3D2E')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'rgba(15,22,32,0.5)')}
                  >
                    {h.text}
                  </a>
                ))}
              </nav>
            </div>
          </aside>
        )}

        {/* Article */}
        <div className="cf-blog-content">
          <article
            className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-[rgb(22,30,45)] prose-p:text-[rgba(15,22,32,0.7)] prose-p:leading-relaxed prose-a:text-[#0F3D2E] prose-a:font-semibold prose-a:no-underline hover:prose-a:underline prose-strong:text-[rgb(22,30,45)] prose-code:bg-white prose-code:border prose-code:border-gray-200 prose-code:rounded prose-code:px-1 prose-code:py-0.5 prose-code:text-[#0F3D2E] prose-pre:bg-[rgb(7,40,29)] prose-pre:text-gray-100 prose-li:text-[rgba(15,22,32,0.7)] prose-hr:border-[rgba(15,61,46,0.1)]"
            style={{ fontFamily: FONT }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      </div>

      {/* Share + back link */}
      <div className="px-4 py-10 border-t" style={{ borderColor: 'rgba(15,61,46,0.1)' }}>
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <ShareButtons url={postUrl} title={post.title} theme="dark" />
          <Link to="/blog" className="text-sm font-semibold" style={{ color: '#0F3D2E', fontFamily: FONT }}>
            ← Back to Blog
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
